// ABOUTME: Stacked bar chart showing behavior type counts per time period (Chart 4 in FamilyInsightsPage)
// ABOUTME: Issue #1608: wires up BehaviorBreakdownOverTime API endpoint from #1605

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { BehaviorType } from '../types/api';
import { getBehaviorColor, getBehaviorLabel } from '../utils/behaviorUtils';
import theme from '../theme/theme';
import naluV1Theme from '../theme/theme.nalu-v1';
import { analyticsAPI } from '../services/api';
import { getMobileChartMargins, MOBILE_BREAKPOINT_QUERY } from '../utils/mobileOptimization';
import { getDateRange } from './BehaviorBreakdownChartUtils';

export interface BehaviorBreakdownChartProps {
  familyId: number;
  childId: number | 'all';
  tab: 'Week' | 'Month' | '3 Months' | '6 Months' | 'All Time';
  familyTimezone: string | undefined;
  familyCreatedAt: string | undefined;
  isBuildingBaseline: boolean;
}

// Behavior type order for stacking (positive at bottom, interventions at top)
const BEHAVIOR_STACK_ORDER = [
  BehaviorType.Star,
  BehaviorType.Triangle,
  BehaviorType.Square,
  BehaviorType.FlipYourCoin,
  BehaviorType.MakeItRight,
  BehaviorType.OneOnOneTime,
] as const;

// Map BehaviorType enum values to API response field names (GoalNuke not included in API response)
const BEHAVIOR_TYPE_FIELDS: Record<BehaviorType, string> = {
  [BehaviorType.Star]: 'stars',
  [BehaviorType.Triangle]: 'triangles',
  [BehaviorType.Square]: 'squares',
  [BehaviorType.FlipYourCoin]: 'flipYourCoins',
  [BehaviorType.MakeItRight]: 'makeItRights',
  [BehaviorType.OneOnOneTime]: 'oneOnOneTimes',
  [BehaviorType.GoalNuke]: 'goalNukes',
  // Reset is a neutral Crossing (Issue #1937) — not part of the stacked breakdown; mapped only to satisfy the type
  [BehaviorType.Reset]: 'resets',
};

type Granularity = 'day' | 'week' | 'month';

function getGranularity(tab: BehaviorBreakdownChartProps['tab']): Granularity {
  switch (tab) {
    case 'Week':
    case 'Month':
      return 'day';
    case '3 Months':
    case '6 Months':
      return 'week';
    case 'All Time':
      return 'month';
  }
}

// Generate all expected period slots between startDate and endDate (inclusive)
// so the chart shows empty bars for days/weeks/months with no data
function generateAllPeriods(
  startDate: string,
  endDate: string,
  granularity: Granularity
): string[] {
  const periods: string[] = [];
  const startParts = startDate.split('-').map(Number);
  const endParts = endDate.split('-').map(Number);
  const current = new Date(startParts[0], startParts[1] - 1, startParts[2]);
  const end = new Date(endParts[0], endParts[1] - 1, endParts[2]);

  while (current <= end) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, '0');
    const dd = String(current.getDate()).padStart(2, '0');
    periods.push(`${yyyy}-${mm}-${dd}`);

    if (granularity === 'day') {
      current.setDate(current.getDate() + 1);
    } else if (granularity === 'week') {
      current.setDate(current.getDate() + 7);
    } else {
      current.setMonth(current.getMonth() + 1);
    }
  }
  return periods;
}

// Merge API results into a full set of period slots, filling gaps with zeros
function fillMissingPeriods(
  data: PeriodData[],
  startDate: string,
  endDate: string,
  granularity: Granularity
): PeriodData[] {
  const allPeriods = generateAllPeriods(startDate, endDate, granularity);
  const dataByPeriod = new Map(data.map(d => [d.period, d]));
  const emptyPeriod: Omit<PeriodData, 'period'> = {
    stars: 0, triangles: 0, squares: 0,
    flipYourCoins: 0, makeItRights: 0, oneOnOneTimes: 0,
  };

  return allPeriods.map(period => dataByPeriod.get(period) ?? { period, ...emptyPeriod });
}

// Full label used in tooltip (e.g. "Week of Jan 26", "Jan 2026")
function formatPeriodLabel(period: string, granularity: Granularity): string {
  // period is a YYYY-MM-DD string from the API; construct local date to avoid UTC offset shift
  const parts = period.split('-').map(Number);
  const date = new Date(parts[0], parts[1] - 1, parts[2]);

  if (granularity === 'month') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  if (granularity === 'week') {
    return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Short label used on the X axis tick to avoid crowding (no "Week of" prefix)
function formatAxisTickLabel(period: string, granularity: Granularity): string {
  const parts = period.split('-').map(Number);
  const date = new Date(parts[0], parts[1] - 1, parts[2]);
  if (granularity === 'month') {
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface PeriodData {
  period: string;
  stars: number;
  triangles: number;
  squares: number;
  flipYourCoins: number;
  makeItRights: number;
  oneOnOneTimes: number;
}

// Custom tooltip showing per-type counts on hover
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTooltip = ({ active, payload, label, isNaluV1Theme }: any) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${theme.palette.divider}`,
        // Bare numbers are a multiplier against theme.shape.borderRadius (16 for
        // nalu-v1) under the sx prop, so an explicit px string is required here.
        borderRadius: isNaluV1Theme ? '4px' : 1,
        p: 1.5,
        boxShadow: 2,
      }}
    >
      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
        {label}
      </Typography>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {payload.map((entry: any) => (
        <Typography key={entry.dataKey} variant="caption" display="block" sx={{ color: entry.fill }}>
          {entry.name}: {entry.value}
        </Typography>
      ))}
    </Box>
  );
};

const BehaviorBreakdownChart: React.FC<BehaviorBreakdownChartProps> = ({
  familyId,
  childId,
  tab,
  familyTimezone,
  familyCreatedAt,
  isBuildingBaseline,
}) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT_QUERY);
  const chartMargins = getMobileChartMargins('standard', isMobile);
  const muiTheme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = muiTheme.logoSrc === naluV1Theme.logoSrc;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periods, setPeriods] = useState<PeriodData[]>([]);

  const granularity = useMemo(() => getGranularity(tab), [tab]);
  const { startDate, endDate } = useMemo(
    () => getDateRange(tab, familyTimezone, familyCreatedAt),
    [tab, familyTimezone, familyCreatedAt]
  );

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const childIdParam = childId === 'all' ? undefined : childId;
        const result = await analyticsAPI.getBehaviorBreakdownOverTime(
          familyId,
          startDate,
          endDate,
          granularity,
          familyTimezone,
          childIdParam
        );
        setPeriods(fillMissingPeriods(result || [], startDate, endDate, granularity));
      } catch (err) {
        console.error('Failed to load behavior breakdown data:', err);
        setError('Unable to load behavior breakdown data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [familyId, childId, startDate, endDate, granularity, familyTimezone]);

  if (isBuildingBaseline) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        Keep logging to build your behavior trends.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const chartData = periods.map(p => ({
    ...p,
    periodLabel: formatPeriodLabel(p.period, granularity),
    axisLabel: formatAxisTickLabel(p.period, granularity),
  }));

  const isEmpty = chartData.length === 0 || chartData.every(
    p => BEHAVIOR_STACK_ORDER.every(bt => (p as Record<string, unknown>)[BEHAVIOR_TYPE_FIELDS[bt]] === 0)
  );

  const granularityLabel = granularity === 'day' ? 'Day' : granularity === 'week' ? 'Week' : 'Month';


  return (
    <Box data-testid="behavior-breakdown-chart">
      <Typography variant={isNaluV1Theme ? 'h4' : 'h6'} gutterBottom>
        📊 Behavior Type Breakdown by {granularityLabel}
      </Typography>
      {isEmpty && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, textAlign: 'center' }}
        >
          No behaviors recorded for this period.
        </Typography>
      )}
      <Box sx={{ height: 340, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={chartMargins}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="axisLabel"
              fontSize={11}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={70}
            />
            <YAxis
              label={{ value: 'Behaviors', angle: -90, position: 'insideLeft', fontSize: 12 }}
              allowDecimals={false}
              fontSize={12}
            />
            <Tooltip content={<CustomTooltip isNaluV1Theme={isNaluV1Theme} />} />
            <Legend />
            {BEHAVIOR_STACK_ORDER.map(behaviorType => (
              <Bar
                key={behaviorType}
                dataKey={BEHAVIOR_TYPE_FIELDS[behaviorType]}
                name={getBehaviorLabel(behaviorType)}
                stackId="behaviors"
                fill={getBehaviorColor(behaviorType)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default BehaviorBreakdownChart;
